import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Ban,
  CheckCheck,
  CalendarClock,
  Eye,
  LucideIcon,
  MessagesSquare,
  Radar,
  TrendingUp,
  TriangleAlert,
  UserPlus,
  UserRoundCheck,
  Zap,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import type { ArdiActivityItem } from '../../../common/hooks/use-ardi-api.hook';

/**
 * Ardi's log, as lines a person reads: "Noticed 34 shoppers in an hour — 4×
 * your usual." The API stores a key and facts; the words are here, in the
 * reader's language.
 */
const ICONS: Record<string, LucideIcon> = {
  TRAFFIC_SPIKE: Zap,
  PRODUCT_TRENDING: TrendingUp,
  TRACKING_SILENT: TriangleAlert,
  TRACKING_BACK: Activity,
  LEAD_NEW: UserPlus,
  LEAD_RETURNED: UserRoundCheck,
  ROUTINE_RAN: CalendarClock,
  ROUTINE_FAILED: CalendarClock,
  CHANNEL_CONNECTED: MessagesSquare,
  CHANNEL_ANSWERED: MessagesSquare,
  CHANNEL_APPROVED: CheckCheck,
  CHANNEL_DECLINED: Ban,
  WEEKLY_REVIEW: Radar,
};

export const activityIcon = (key: string): LucideIcon => ICONS[key] ?? Eye;

const channelName = (kind?: string) => (kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : '');

export function activityParams(item: ArdiActivityItem): Record<string, any> {
  const d = item.data ?? {};
  return {
    ...d,
    channel: d.name || channelName(d.kind),
    who: d.who || '',
    email: d.email || '',
    product: d.productName || d.topProduct || '',
    title: d.title || '',
    question: d.question || '',
  };
}

export function ArdiActivityFeed({ items, compact, emptyText }: { items: ArdiActivityItem[]; compact?: boolean; emptyText?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!items.length) {
    return <p className="py-6 text-center text-xs text-muted-foreground">{emptyText ?? t('ARDI.ACTIVITY.EMPTY')}</p>;
  }

  return (
    <ol className="relative flex flex-col" data-testid="ardi-activity">
      {items.map((item, i) => {
        const Icon = activityIcon(item.key);
        const warn = item.key === 'TRACKING_SILENT' || item.key === 'ROUTINE_FAILED';
        const params = activityParams(item);
        return (
          <li key={item._id} className="relative flex gap-3 pb-4 last:pb-0">
            {/* The thread between entries: a log, not a list of cards. */}
            {i < items.length - 1 && <span className="absolute start-[15px] top-8 bottom-0 w-px bg-border" aria-hidden />}
            <span className={cn('relative z-10 flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background', warn ? 'text-warning' : 'text-muted-foreground')}>
              <Icon className="size-4" aria-hidden />
            </span>
            <button
              type="button"
              disabled={!item.route}
              onClick={() => item.route && navigate(item.route)}
              className="min-w-0 flex-1 rounded-md pt-0.5 text-start enabled:hover:text-foreground disabled:cursor-default"
            >
              <span className="block text-sm leading-snug">{t(`ARDI.ACTIVITY.${item.key}`, params) as string}</span>
              {!compact && params.question && item.key === 'CHANNEL_ANSWERED' && (
                <span className="mt-0.5 block line-clamp-1 text-xs text-muted-foreground">“{params.question}”</span>
              )}
              <span className="mt-0.5 block text-[11px] text-muted-foreground">
                <RelativeTime value={item.createdAt} />
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
